import {
  ShadowMode,
  Ellipsoid,
  Cartesian3,
  Ray,
  Scene,
  PerspectiveFrustum,
  ImageryLayer,
  DirectionalLight,
  Math as CesiumMath,
  Entity,
} from "@cesium/engine";
import React, { useEffect, useMemo, useRef } from "react";
import { useCesiumContext } from "./CesiumContext";
import {
  cartesianFromPolarCentric,
  cartesianToPolarCentric,
} from "src/lib/cesium/adjustCartesian";
import { useCesiumMap } from "./map";
import QMViewer from "src/lib/cesium/QuickMapViewer";
import { EyeNoneIcon, EyeOpenIcon } from "@radix-ui/react-icons";
import { Wave } from "../../vendor/better-react-spinkit";
import { debounce } from "lodash-es";
import { useClock } from "../clock";
import { isDefined } from "../../lib/utils";
import Button from "../base/Button";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { cn } from 'src/lib/utils';
import { getEntityName } from "./TrackedEntityMenu";
import { MODEL_OFFSETS } from "src/constants";

export function findTerrainIntercept(
  startPoint: Cartesian3,
  destinationPoint: Cartesian3,
  scene: Scene
) {
  // ray direction
  let rayDir = new Cartesian3();
  rayDir = Cartesian3.subtract(destinationPoint, startPoint, rayDir);
  if(Cartesian3.magnitude(rayDir) > 0) {
    rayDir = Cartesian3.normalize(rayDir, rayDir);
  }
  // find intercept point between the camera and the body position
  const ray = new Ray(startPoint, rayDir);
  const interceptPoint = scene.globe.pick(ray, scene);

  return interceptPoint;
}


export function findCamToEntityTerrainIntercept(
  cameraPos: Cartesian3,
  entityPos: Cartesian3,
  scene: Scene
) {
  const interceptPoint = findTerrainIntercept(cameraPos, entityPos, scene);
  if (isDefined(interceptPoint)) {
    // check whether the intersect point is between camera and ref point
    const cam2RefVect = new Cartesian3();
    Cartesian3.subtract(entityPos, cameraPos, cam2RefVect);
    const cam2InterceptVect = new Cartesian3();
    Cartesian3.subtract(interceptPoint, cameraPos, cam2InterceptVect);
    if (
      Cartesian3.magnitude(cam2InterceptVect) <
      Cartesian3.magnitude(cam2RefVect)
    ) {
      // the interception happens nearer than the ref point location
      return interceptPoint;
    }
  }
  // no intercecption between camera and entity
  return undefined;
}

function syncEntities(source: QMViewer, dest: QMViewer) {
  //dest.entities.removeAll();
  const length = source.entities.values.length;
  const activeEntities: Entity[] = [];

  for (let i = 0; i < length; i++) {
    const entity = source.entities.values[i];
    if (entity.show === true) {
      activeEntities.push(entity);
    }
  }
  for (let i = 0; i < activeEntities.length; i++) {
    const entity = activeEntities[i];
    if (!dest.entities.contains(entity)) {
      dest.entities.add(entity);
    }
  }
  for (let i = 0; i < dest.entities.values.length; i++) {
    const entity = dest.entities.values[i];
    if (activeEntities.indexOf(entity) === -1) {
      dest.entities.remove(entity);
    }
  }
}

function getSyncImageryLayersFn(source: QMViewer, dest: QMViewer) {
  return debounce(() => {
    const length = source.imageryLayers.length;
    const activeLayers: ImageryLayer[] = [];
    for (let i = 0; i < length; i++) {
      const sourceLayer = source.imageryLayers.get(i);
      if (sourceLayer.show === false) continue;
      activeLayers.push(sourceLayer);
    }
    activeLayers.forEach((l, i) => {
      const destLayer = dest.imageryLayers.get(i);
      if (destLayer && l.imageryProvider === destLayer.imageryProvider) return;
      const newLayer = new ImageryLayer(l.imageryProvider, {});
      dest.imageryLayers.add(newLayer, i);
    });
    while (dest.imageryLayers.length > activeLayers.length) {
      dest.imageryLayers.remove(
        dest.imageryLayers.get(dest.imageryLayers.length - 1)
      );
    }
  }, 100);
}

export function getNadirTrackFn(source: QMViewer, dest: QMViewer) {
  return () => {
    if (!source || source.isDestroyed()) return;
    if (!dest || dest.isDestroyed()) return;
    const camera = source.camera;
    let [cameraDir, cameraUp] = source.getCameraLookNadirVectors();
    let cameraPos = camera.positionWC;
    if (dest.isPolarCentric !== source.isPolarCentric) {
      // adjust coordinates for reference system
      const convert = dest.isPolarCentric
        ? cartesianFromPolarCentric
        : cartesianToPolarCentric;
      cameraPos = convert(cameraPos);
      cameraDir = convert(cameraDir);
      cameraUp = convert(cameraUp);
    }

    dest.camera.position = cameraPos;
    dest.camera.direction = cameraDir;
    dest.camera.up = cameraUp;
  };
}
export function getTrackBodyFn(
  source: QMViewer,
  dest: QMViewer,
  { bodyName }: { bodyName: string }
) {
  return () => {
    let bodyPos = source.qtsLight?.getBodySampledPosition(bodyName);
    if (!bodyPos) return;
    let cameraPos = source.camera.positionWC;
    // @ts-ignore
    let lightVec = source.scene.light?.direction;
    if (dest.isPolarCentric !== source.isPolarCentric) {
      // adjust coordinates for reference system
      const convert = dest.isPolarCentric
        ? cartesianFromPolarCentric
        : cartesianToPolarCentric;
      cameraPos = convert(cameraPos);
      bodyPos = convert(bodyPos);
      lightVec = convert(lightVec);
    }
    dest.camera.position = cameraPos;
    dest.lookAtPos(bodyPos);
    // needed for light effects on models
    if (dest.scene.light instanceof DirectionalLight) {
      dest.scene.light.direction = lightVec;
    }
  };
}

export function getTrackEntityFn(
  source: QMViewer,
  dest: QMViewer,
  { entityId, datasourceId }: { entityId: string; datasourceId?: string }
) {
  return () => {
    const entity = source.getEntity({ entityId, datasourceId });
    if (!entity) return;
    const modelUri = entity.model?.uri?.getValue(source.clock.currentTime);
    const entityOffset = MODEL_OFFSETS[modelUri] ?? Cartesian3.ZERO;
    let entityPos = source.getEntityPosition(entity, source.clock.currentTime, entityOffset);
    if (!entityPos) return;
    let cameraPos = Cartesian3.clone(source.camera.positionWC);
    let cameraDir = Cartesian3.clone(source.camera.directionWC);
    let cameraUp = Cartesian3.clone(source.camera.upWC);
    // @ts-ignore
    let lightVec = source.scene.light?.direction;
    if (dest.isPolarCentric !== source.isPolarCentric) {
      // adjust coordinates for reference system
      const convert = dest.isPolarCentric
        ? cartesianFromPolarCentric
        : cartesianToPolarCentric;
      cameraPos = convert(cameraPos);
      entityPos = convert(entityPos);
      lightVec = convert(lightVec);
      cameraDir = convert(cameraDir);
      cameraUp = convert(cameraUp);
    }
    dest.camera.position = cameraPos;
    if (source.trackedEntity === entity && Cartesian3.distance(cameraPos, entityPos) <= 1) {
      // camera is at the entity position
      // let us use the source view camera orientation
      dest.camera.setView({
        orientation: {
          direction: cameraDir,
          up: cameraUp
        }
      });
    } else {
      dest.lookAtPos(entityPos);
    }
    // needed for light effects on models
    if (dest.scene.light instanceof DirectionalLight) {
      dest.scene.light.direction = lightVec;
    }
  };
}
export function MapSceneWrapper({
  planet,
  id,
  title,
  getTrackingFn,
  trackingOpts,
  showIsVisible,
  imagerySyncEnabled,
  fov,
  refColor,
  onClose,
}: Omit<MapSceneProps, "map">) {
  const { map } = useCesiumContext();
  if (!map) return;
  return (
    <MapScene
      map={map}
      planet={planet}
      id={id}
      title={title}
      getTrackingFn={getTrackingFn}
      trackingOpts={trackingOpts}
      showIsVisible={showIsVisible}
      fov={fov}
      refColor={refColor}
      imagerySyncEnabled={imagerySyncEnabled}
      onClose={onClose}
    />
  );
}

const DEFAULT_FOV = CesiumMath.toRadians(60);
type MapSceneProps = {
  children?: React.ReactNode;
  map: QMViewer;
  planet: string;
  id: string;
  title?: string;
  getTrackingFn?: (
    source: QMViewer,
    dest: QMViewer,
    trackingOpts?: { [key: string]: any }
  ) => () => void;
  trackingOpts?: { [key: string]: any };
  showIsVisible?: boolean;
  imagerySyncEnabled?: boolean;
  fov?: number;
  refColor?: string;
  onClose?: () => void;
};
export function MapScene(props: MapSceneProps) {
  const infoDivRef = useRef<HTMLDivElement>(null);
  const clock = useClock();
  const mapElement = React.useRef(null);
  const {
    children,
    map,
    getTrackingFn,
    trackingOpts,
    title,
    showIsVisible,
    imagerySyncEnabled = true,
    fov = DEFAULT_FOV,
    refColor,
    id,
    onClose,
    ...options
  } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const sceneViewer = useCesiumMap(mapElement, {
    geocoder: false,
    projectionPicker: false,
    infoBox: false,
    selectionIndicator: false,
    skyAtmosphere: false,
    shadows: false,
    terrainShadows: ShadowMode.DISABLED,
    scene3DOnly: true,
    // forcing the base layer picker to be enabled as if it is not enabled
    // the sun/earth culling is not properly working?!
    // it will be hidden after creating the viewer
    baseLayerPicker: true,
    imageryProviderViewModels: [],
    terrainProviderViewModels: [],
    homeButton: false,
    navigationHelpButton: false,
    timeline: false,
    fullscreenButton: false,
    skyBox: false,
    animation: false,
    ellipsoid: Ellipsoid.default,
    ...options,
    disableQTSLight: true,
    disableQTSNavigation: true,
    clock,
  });
  const entity = useMemo(() => {
    if (!trackingOpts?.entityId) return;
    return map.getEntity({
      entityId: trackingOpts.entityId,
      datasourceId: trackingOpts.datasourceId,
    });
  }, [map, trackingOpts]);
  useEffect(() => {
    if (!map || !sceneViewer) return;

    sceneViewer.scene.globe.enableLighting = false;
    // Hide the image picker. It was enabled for fixing bodies culling issues?!
    // @ts-ignore
    sceneViewer.baseLayerPicker.container.style.display = "none";
    // set terrain provider
    sceneViewer.defaultTerrainProvider = map._defaultTerrainProvider;
    sceneViewer.polarCentricTerrainProvider = map._polarCentricTerrainProvider;
    sceneViewer.isAutoPolarSwitchingEnabled = true;
    // Since the view follows the main view, we disable any
    // camera movement on it
    sceneViewer.scene.screenSpaceCameraController.enableRotate = false;
    sceneViewer.scene.screenSpaceCameraController.enableTranslate = false;
    sceneViewer.scene.screenSpaceCameraController.enableZoom = false;
    sceneViewer.scene.screenSpaceCameraController.enableTilt = false;
    sceneViewer.scene.screenSpaceCameraController.enableLook = false;
    if (sceneViewer.scene.camera.frustum instanceof PerspectiveFrustum) {
      sceneViewer.scene.camera.frustum.fov = fov;
    }
    const syncImageryLayers = getSyncImageryLayersFn(map, sceneViewer);
    let cancelLayerSyncFn: () => void;
    if (imagerySyncEnabled) {
      syncImageryLayers();
      cancelLayerSyncFn =
        map.scene.globe.imageryLayersUpdatedEvent.addEventListener(
          syncImageryLayers
        );
    }
    const updateEntities = () => {
      syncEntities(map, sceneViewer);
      if (entity && !sceneViewer.entities.contains(entity)) {
        const entityCollectionBak = entity.entityCollection;
        sceneViewer.entities.add(entity);
        // restore previews entity collection
        // NOTE: AVOID TO UPDATE THE ENTITY ENTITYCOLLECTION. IT MIGHT BE THE DATA SOURCE ENTITY COLLECTION
        entity.entityCollection = entityCollectionBak;
      }
    }
    // NOTE: Do we really want to sync all entities?
    // Entities can be quite harmful to performance. Maybe only sync any tracked entity.
    updateEntities();
    const cancelEntitySyncFn =
      map.entities.collectionChanged.addEventListener(updateEntities);

    const trackFn = getTrackingFn?.(map, sceneViewer, trackingOpts);
    let isVisibleFn: () => void | undefined;
    if (trackingOpts?.bodyName) {
      isVisibleFn = () => {
        const bodyPos = map.qtsLight?.getBodySampledPosition(
          trackingOpts.bodyName
        );
        if (!bodyPos) return setIsVisible(false);
        const intercept = findTerrainIntercept(
          sceneViewer.camera.positionWC,
          bodyPos,
          sceneViewer.scene
        );
        setIsVisible(!intercept);
      };
    } else if (trackingOpts?.entityId) {
      isVisibleFn = () => {
        if (!entity) return setIsVisible(false);
        const modelUri = entity.model?.uri?.getValue(map.clock.currentTime);
        const entityOffset = MODEL_OFFSETS[modelUri] ?? Cartesian3.ZERO; 
        const entityPos = map.getEntityPosition(entity, sceneViewer.clock.currentTime, entityOffset);
        if (!entityPos) return setIsVisible(false);
        const intercept = findCamToEntityTerrainIntercept(
          sceneViewer.camera.positionWC,
          entityPos,
          sceneViewer.scene
        );
        setIsVisible(!intercept);
      };
    }
    map.registerAuxViewer(id, sceneViewer);
    const qtsLight = map.qtsLight;
    const cancelViewSyncFn = map.scene.preRender.addEventListener(() => {
      trackFn?.();
      isVisibleFn?.();
      const bodyName = trackingOpts?.bodyName;
      if (infoDivRef.current && isDefined(qtsLight) && isDefined(bodyName)) {
        // update more info
        const elevationAngleRad = qtsLight.getBodyElevationAngle(bodyName);
        const azimuthAngleRad = qtsLight.getBodyAzimuthAngle(bodyName);
        const elevationAngleStr =
          elevationAngleRad != null
            ? CesiumMath.toDegrees(elevationAngleRad).toFixed(2)
            : "N/A";
        const azimuthAngleStr =
          azimuthAngleRad != null
            ? CesiumMath.toDegrees(azimuthAngleRad).toFixed(2)
            : "N/A";
        infoDivRef.current.innerHTML = `el.,az.: ${elevationAngleStr}°, ${azimuthAngleStr}°`;
      }
    });

    const cancelTileLodingFn =
      sceneViewer.scene.globe.tileLoadProgressEvent.addEventListener(
        (count) => {
          setIsLoading(count > 0);
        }
      );

    if (isDefined(qtsLight)) {
      const lightSource = qtsLight?.source;
      if (lightSource) {
        sceneViewer.scene.light = new DirectionalLight({
            direction:
            qtsLight.bodies[lightSource].getLightDirection(map.clock.currentTime) ??
              new Cartesian3(1, 1, 1),
            color: qtsLight?.lightSourceOptions[lightSource].lightParams.lightColor,
            intensity: qtsLight?.lightSourceOptions[lightSource].lightParams.lightIntensity,
          });
        }
      }
    return () => {
      cancelLayerSyncFn?.();
      cancelEntitySyncFn?.();
      cancelViewSyncFn?.();
      cancelTileLodingFn?.();
      map.deregisterAuxViewer(id);
    };
  }, [map, sceneViewer, imagerySyncEnabled, entity]);

  let entityTitle;
  if (entity) {
    entityTitle = getEntityName(entity);
  }
  return (
    <div
      className="w-[200px] h-[200px] border shadow-md relative"
      ref={mapElement}
    >
      <div className="absolute top-0 left-0 m-1 z-30 text-xs w-full h-full flex flex-col">
        <div className="flex flex-row items-center mx-1">
          <div className="pr-2">{title ?? entityTitle}</div>
          {showIsVisible ? <VisibleIcon value={isVisible} /> : null}
          <div className={cn("mr-2", { invisible: !isLoading })}>
            <Wave size={15} />
          </div>
          {onClose && (
            <div className="ml-auto">
              <Button size="sq" variant="secondary" isQuiet onPress={onClose}>
                <XMarkIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        <div
          className="font-mono text-[10px] mx-1 mb-2 mt-auto"
          ref={infoDivRef}
        ></div>
      </div>
      {refColor && 
        <div className="absolute bottom-0 left-0 m-2 z-30 rounded-full border w-3 h-3" style={{borderColor: refColor, backgroundColor: refColor}} title="Tracked entity reference color"/>}
      {children}
    </div>
  );
}

export function VisibleIcon({ value }: { value: boolean }) {
  return value ? (
    <EyeOpenIcon className="text-green-500 w-4 h-4" />
  ) : (
    <EyeNoneIcon className="text-red-500 w-4 h-4" />
  );
}
